import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import StateCheckboxSelector, {
  US_STATES, normalizeStateInput,
} from "./StateCheckboxSelector";

/**
 * Iter-61 — Destination-level state allowlist + per-state fees.
 *
 * Extracted from `components/delivery/fields/EmailFields.jsx` so
 * every destination type (email, webhook, all 8 CRMs, Zapier, S3,
 * FTP) can render it consistently. Backward-compat: existing
 * destinations with a comma-separated OR list-shaped
 * `config.state_allowlist` load into the checkbox UI without
 * migration.
 *
 * Inheritance model (Iter-61):
 *   * "Use buyer default states" (default for NEW destinations) —
 *     `config.state_allowlist` is stored as [] and the routing
 *     engine relies on the buyer's own `intake_criteria.accepted_states`.
 *   * "Override for this destination" — `config.state_allowlist`
 *     holds an explicit list. Server-side validation ensures the
 *     override is a subset of the buyer's accepted states when the
 *     buyer has any configured.
 *
 * Existing destinations with a non-empty `state_allowlist` render
 * as "Override for this destination" so existing behaviour is
 * preserved to the byte.
 */

const CODE_TO_NAME = new Map(US_STATES.map((s) => [s.code, s.name]));

// The list-vs-string legacy inputs come in via `config.state_allowlist`.
// Fees also survive both shapes.
function parseFeesText(text) {
  const out = {};
  if (!text) return out;
  for (const pair of String(text).split(",")) {
    const t = pair.trim();
    if (!t) continue;
    const [k, v] = t.split(":");
    if (!k || !v) continue;
    const code = k.trim().toUpperCase();
    const fee = Number(String(v).trim());
    if (Number.isFinite(fee) && fee >= 0) out[code] = fee;
  }
  return out;
}
function feesToText(fees) {
  if (!fees || typeof fees !== "object") return "";
  return Object.entries(fees)
    .filter(([k, v]) => k && Number.isFinite(Number(v)))
    .map(([k, v]) => `${k}:${v}`)
    .join(", ");
}


export default function StateAllowlistFields({
  config,
  setConfig,
  buyerAcceptedStates,   // string[] | null — buyer's `intake_criteria.accepted_states`
}) {
  const buyerSet = useMemo(
    () => (buyerAcceptedStates && buyerAcceptedStates.length
      ? new Set(buyerAcceptedStates)
      : null),
    [buyerAcceptedStates],
  );

  // Iter-61 inheritance: existing dests with any state_allowlist boot
  // as "Override", new ones default to "Use buyer default".
  const initialMode = useMemo(() => {
    const cur = normalizeStateInput(config?.state_allowlist);
    return cur.length ? "override" : "buyer";
  }, []); // intentionally empty — only on mount, to preserve legacy semantics.
  const [mode, setMode] = useState(initialMode);

  const selectedStates = useMemo(
    () => normalizeStateInput(config?.state_allowlist),
    [config?.state_allowlist],
  );

  const [defaultPrice, setDefaultPrice] = useState(350);
  useEffect(() => {
    api.get("/platform")
      .then((r) => setDefaultPrice(Number(r.data?.default_lead_price) || 350))
      .catch(() => {});
  }, []);

  /**
   * Iter-62.1.2 — Live-edit source-of-truth.
   *
   * The pre-fix memo preferred `config.state_fees` (object) whenever
   * it was present. On Edit hydration, `DeliveryDestinations.edit()`
   * seeds BOTH keys: the object shape from the API AND the text form
   * derived from it. `setFee()` writes only to `state_fees_text`, so
   * the object stayed stale and every user keystroke got snapped
   * back to the hydrated value.
   *
   * Corrected precedence:
   *   * If `state_fees_text` has been initialized on the form
   *     (typeof === "string", including empty string after
   *     `switchMode("buyer")`), text is the authoritative editing
   *     source — parsed live on every render so `setFee` always
   *     drives the controlled input value.
   *   * Fall back to `config.state_fees` only for pure legacy
   *     hydration when `state_fees_text` has never been set on the
   *     form (should not happen in the current DeliveryDestinations
   *     edit flow, but kept as a defensive fallback for other
   *     mount paths).
   */
  const fees = useMemo(() => {
    if (typeof config?.state_fees_text === "string") {
      return parseFeesText(config.state_fees_text);
    }
    if (config?.state_fees && typeof config.state_fees === "object") {
      return config.state_fees;
    }
    return {};
  }, [config?.state_fees, config?.state_fees_text]);

  const setStates = (nextArr) => {
    setConfig("state_allowlist", nextArr);
    setConfig("state_fees_text", feesToText({
      // Drop fees for states no longer selected.
      ...Object.fromEntries(
        Object.entries(fees).filter(([code]) => nextArr.includes(code)),
      ),
    }));
  };

  const setFee = (code, val) => {
    const next = { ...fees };
    if (val === "" || val == null) delete next[code];
    else next[code] = Number(val);
    setConfig("state_fees_text", feesToText(next));
  };

  const switchMode = (m) => {
    setMode(m);
    if (m === "buyer") {
      setConfig("state_allowlist", []);
      setConfig("state_fees_text", "");
    }
  };

  const fillFromDefault = () => {
    const next = {};
    for (const code of selectedStates) next[code] = defaultPrice;
    setConfig("state_fees_text", feesToText(next));
  };

  const invalidOverrides = useMemo(() => {
    if (mode !== "override" || !buyerSet) return [];
    return selectedStates.filter((c) => !buyerSet.has(c));
  }, [mode, buyerSet, selectedStates]);

  return (
    <div className="col-span-2" data-testid="state-allowlist-fields">
      <label className="label-mono">Geographic filter</label>

      <div className="flex flex-wrap items-center gap-4 mt-2">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="state-mode"
            value="buyer"
            checked={mode === "buyer"}
            onChange={() => switchMode("buyer")}
            className="w-4 h-4"
            data-testid="state-mode-buyer"
          />
          <span className="text-xs">
            Use buyer default states{" "}
            <span className="text-zinc-500">
              (
              {buyerSet
                ? `${buyerSet.size} states from the buyer's intake criteria`
                : "buyer has no state restriction — destination fires on every lead in-scope"}
              )
            </span>
          </span>
        </label>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="state-mode"
            value="override"
            checked={mode === "override"}
            onChange={() => switchMode("override")}
            className="w-4 h-4"
            data-testid="state-mode-override"
          />
          <span className="text-xs">Override for this destination</span>
        </label>
      </div>

      {mode === "override" && (
        <div className="mt-3 space-y-3">
          <StateCheckboxSelector
            value={selectedStates}
            onChange={setStates}
            testId="dest-state-selector"
            restrictTo={buyerSet || undefined}
            helpText={
              buyerSet
                ? "You can only select states that are in the buyer's accepted-states list. " +
                  "Adding a state outside the buyer's list will be rejected on save."
                : "Buyer has no state restriction — you may pick any supported state."
            }
          />
          {invalidOverrides.length > 0 && (
            <div className="border border-red-700 bg-red-50 text-red-900 px-3 py-2 text-xs" data-testid="dest-state-invalid">
              These states are NOT in the buyer's accepted list and will
              be rejected on save: {invalidOverrides.join(", ")}
            </div>
          )}

          {selectedStates.length > 0 && (
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <label className="label-mono">Per-state fees (${defaultPrice} = platform default)</label>
                <button
                  type="button"
                  onClick={fillFromDefault}
                  data-testid="dest-fees-fill-default"
                  className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
                >
                  Fill from default (${defaultPrice})
                </button>
                <span className="text-[10px] text-zinc-500">
                  Each buyer can be priced differently. Empty = auto-uses platform default at save time.
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
                {selectedStates.map((code) => (
                  <label key={code} className="inline-flex items-center gap-2 text-xs">
                    <span className="font-mono w-24 shrink-0">
                      {code} <span className="text-zinc-500">{CODE_TO_NAME.get(code) || ""}</span>
                    </span>
                    <span className="text-zinc-500">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="w-24 border border-black px-2 py-1 text-xs font-mono"
                      value={fees[code] ?? ""}
                      onChange={(e) => setFee(code, e.target.value)}
                      data-testid={`dest-fee-${code}`}
                      placeholder={`${defaultPrice}`}
                    />
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
